import { Navigate } from 'react-router-dom';
import { useCompany } from '../../hooks/useCompany';
import { useAuthStore } from '../../stores/auth.store';
import { canCompleteCompanyRegistration } from '../../lib/roles';
import { EmptyState } from '../EmptyState';

/**
 * RequireActiveCompany — firma kaydı (KYB) tamamlanana kadar operasyonel
 * rotaları kilitler. Firma `status === 'PENDING_KYB'` iken davranış role-aware:
 *
 *   - Kaydı tamamlayabilen rol (OWNER/CEO/CFO) → `/firma-kaydi` sayfasına yönlendir.
 *   - Diğer roller (`/firma-kaydi`'ye erişemeyen EMPLOYEE/ACCOUNTANT/DEPARTMENT_MANAGER
 *     vb.) → YÖNLENDİRME YOK; bilgilendirici tam-sayfa durum render edilir. Bu roller
 *     `/firma-kaydi`'ye RequireRole ile giremediği için redirect döngüsünü önler.
 *
 * Yükleme sırasında `null` render eder (yanlış yönlendirmeyi önlemek için).
 * Query hata verirse (yetki/ağ) kilit uygulamaz — backend zaten authoritative,
 * FE yalnızca UX kolaylığı sağlar.
 *
 * Mevcut guard'larla iç içe kullanılabilir (RequireAbility/RequireRole içine sar):
 *   <RequireAbility action="read" subject="Budget">
 *     <RequireActiveCompany>
 *       <BudgetsPage />
 *     </RequireActiveCompany>
 *   </RequireAbility>
 */
export function RequireActiveCompany({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useCompany();
  const role = useAuthStore((s) => s.user?.role ?? null);

  // Firma durumu belli değilken hiçbir şey render etme (flicker + yanlış redirect önlenir).
  if (isLoading) return null;

  if (data?.status === 'PENDING_KYB') {
    // Kaydı tamamlayabilen rol → firma kaydı sayfasına yönlendir.
    if (canCompleteCompanyRegistration(role)) {
      return <Navigate to="/firma-kaydi" replace />;
    }
    // Diğer roller → yönlendirme yok (döngü olurdu); bilgilendirici durum göster.
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="card-custel w-full max-w-md">
          <EmptyState
            title="Firma kaydınız onay bekliyor"
            description="Kart, çalışan ve işlem özelliklerini kullanabilmek için yöneticiniz firma kaydını tamamlamalı."
          />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
